import type { DragEventHandler } from 'react';
import classNames from 'classnames';
import _ from 'lodash';
import AddLogButton from './AddLogButton';
import TimeLog from './TimeLog';
import TimeLogGhost from './TimeLogGhost';
import WatermarkHoliday from '../../../../assets/images/watermark_holiday.svg';
import WatermarkVacation from '../../../../assets/images/watermark_vacation.svg';
import type { CalendarDate, Holiday, Log, Project, User } from '../../../types';

type Props = {
  onOpenAddModal: (date: string) => void,
  onOpenEditModal: (log: Log, date: string) => void,
  onAddDraggedLog: (log: Log, date: string) => void,
  onAddLog: (projectId: any, formData: any) => Promise<any>,
  draggedLog: Log,
  day: CalendarDate,
  logsById: { [k: string]: Log },
  logIds: Array<string | number>,
  projects: Array<Project>,
  onDragEnded: DragEventHandler<HTMLDivElement>,
  onDragEntered: DragEventHandler<HTMLDivElement>,
  dragTargetDate: string,
  holidays: Array<Holiday>,
  selectedUserVacationDates: Array<string>,
  currentUser?: User | null,
}

const DayColumn = (props: Props) => {
  const {
    onOpenAddModal,
    onOpenEditModal,
    day,
    logsById,
    logIds,
    projects,
    onAddDraggedLog,
    draggedLog,
    onAddLog,
    onDragEnded,
    onDragEntered,
    dragTargetDate,
    holidays,
    selectedUserVacationDates,
    currentUser,
  } = props;
  const dailyTotal = _.sum(logIds.map(logId => _.toNumber(logsById[logId].time_spent)));
  const dailyTotalParsed = parseFloat(dailyTotal).toFixed(2);
  const isHoliday = holidays.filter(holiday => holiday.date === day.date).length > 0;
  const isVacation = selectedUserVacationDates.filter(vacation => vacation === day.date).length > 0;
  const isWorkday = day.dayName !== 'Sat' && day.dayName !== 'Sun' && !isHoliday && !isVacation;
  const hoursMissing = dailyTotal < 8;

  const droppingOnSelf = (el: any) => {
    const logItem: HTMLElement | null = el.closest('.edit-log');
    if (logItem && logItem.dataset && _.toInteger(logItem.dataset.logId) === draggedLog.id) {
      return true;
    }

    return false;
  };

  const handleDropLog = (event: any) => {
    if (droppingOnSelf(event.target)) return;

    const formData = {
      project_id: draggedLog.project_id,
      category: draggedLog.category,
      time_spent: draggedLog.time_spent,
      description: draggedLog.description,
      date: props.day.date,
      user_id: draggedLog.user.id,
      overtime: draggedLog.overtime,
      overtime_type: draggedLog.overtime_type,
      incident_id: draggedLog.incident_id,
      incident_start_time: draggedLog.incident_start_time,
      task_id: draggedLog.task_id,
    };
    onAddLog(draggedLog.project_id, formData);
  };

  const showGost = dragTargetDate === day.date && draggedLog;

  return (
    <div
      data-day={day.date}
      className="col table-bordered text-center seven-cols height-limiter"
      onDrop={handleDropLog}
      onDragOver={(e) => e.preventDefault()}
      onDragEnter={onDragEntered}
      onDragEnd={onDragEnded}
    >
      <div className={`total-hours text-center ${isWorkday && hoursMissing ? 'hours-missing' : ''}`}>
        <h5>{`Hours: ${dailyTotal.toString().length >= 4 ? dailyTotalParsed : dailyTotal}h`}</h5>
      </div>
      <div className={classNames("billable", { 'holiday': isHoliday, 'vacation': isVacation })}>
        {isHoliday && (<img className="watermark" src={WatermarkHoliday} alt="holiday" />)}
        {isVacation && (<img className="watermark" src={WatermarkVacation} alt="vacation" />)}
        {logIds.map(logId => (
          <div key={logId}>
            {logsById[logId].project_billable && (
              <TimeLog
                day={day}
                onOpenEditModal={onOpenEditModal}
                onAddDraggedLog={onAddDraggedLog}
                projects={projects}
                log={logsById[logId]}
                key={logId}
                currentUser={currentUser}
              />
            )}
          </div>
        ))}
        {showGost && draggedLog.project_billable && (
          <TimeLogGhost
            projects={projects}
            log={draggedLog}
          />
        )}
      </div>
      <div className="non-billable">
        {showGost && !draggedLog.project_billable && (
          <TimeLogGhost
            projects={projects}
            log={draggedLog}
          />
        )}
        {logIds.map(logId => (
          <div key={logId}>
            {logsById[logId].project_billable === false && (
              <TimeLog
                day={day}
                onOpenEditModal={onOpenEditModal}
                projects={projects}
                log={logsById[logId]}
                key={logId}
                onAddDraggedLog={onAddDraggedLog}
                currentUser={currentUser}
              />
            )}
          </div>
        ))}
      </div>
      <AddLogButton onOpenAddModal={onOpenAddModal} id={day.date} />
    </div>
  );
};

export default DayColumn;
